import React, { useState } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import {
  X, Send, Code, GitBranch, Database, ChevronRight, Plus
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ContactPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const [showSuccess, setShowSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.target);

    try {
      const response = await fetch("https://formspree.io/f/myzzbbpg", {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/json",
        },
      });

      if (response.ok) {
        setShowSuccess(true);
        setFormData({ name: '', email: '', message: '' });
        setIsOpen(false);
        setTimeout(() => setShowSuccess(false), 5000);
      }
    } catch (error) {
      console.error("Error:", error);
    }

    setIsSubmitting(false);
  };

  return (
    <>
      {/* Schedule Button */}
      <motion.button
        className="bg-white text-white text-xl px-10 py-4 rounded-full font-bold
          hover:bg-blue-600 hover:text-black transition-all duration-300"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(true)}
      >
        Schedule a Consultation
      </motion.button>

      {/* Popup Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-white bg-opacity-50  flex items-center justify-center p-4 z-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) setIsOpen(false);
            }}
          >
            {/* Popup Content */}
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.5, opacity: 0 }}
              className="bg-white rounded-2xl shadow-xl w-full  max-w-md relative overflow-hidden"
            >
              {/* Background Elements */}
              <div className="absolute inset-0 overflow-hidden opacity-5">
                <Code className="absolute top-10 left-10 w-16 h-16" />
                <GitBranch className="absolute bottom-20 right-10 w-12 h-12" />
                <Database className="absolute top-1/2 left-1/2 w-20 h-20" />
              </div>

              {/* Close Button */}
              <button
                onClick={() => setIsOpen(false)}
                className="absolute right-4 top-4 text-gray-500 hover:text-black transition-colors duration-200 z-10"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Form Header */}
              <div className="bg-white text-white p-8 relative">
                <span className="text-blue-600 font-bold tracking-wider text-sm uppercase mb-2 block">
                  Get Started
                </span>
                <h2 className="text-3xl font-black mb-2">Schedule a Consultation</h2>
                <p className="text-gray-400">
                  Let's discuss how we can transform your business
                </p>
              </div>

              {/* Form Content */}
              <>
                {showSuccess && (
                  <div className="fixed inset-0 flex items-center justify-center z-50 bg-white/50">
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="bg-white p-6 rounded-xl shadow-lg max-w-md mx-4"
                    >
                      <h3 className="text-xl font-bold text-green-600 mb-2">Message Sent Successfully!</h3>
                      <p className="text-gray-600">We'll get back to you soon.</p>
                      <button
                        onClick={() => setShowSuccess(false)}
                        className="mt-4 px-4 py-2 bg-white text-white rounded-lg hover:bg-blue-600 hover:text-black transition-colors duration-300"
                      >
                        Close
                      </button>
                    </motion.div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="p-8 space-y-6 relative">
                  <div>
                    <label className="block text-sm text-left font-bold text-gray-700 mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all duration-200"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-left font-bold text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all duration-200"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-left font-bold text-gray-700 mb-2">
                      Message
                    </label>
                    <textarea
                      name="message"
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all duration-200 h-32 resize-none"
                      placeholder="Tell us about your project..."
                    />
                  </div>
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-white hover:bg-blue-600 hover:text-black text-white py-4 rounded-lg font-bold transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Send className="w-5 h-5" />
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </motion.button>
                </form>
              </>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const ServiceAreas = () => {
  // ... rest of your existing ServiceAreas component code ...

  return (
    <section className="py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        className="mt-0 text-center"
      >
        <p className="text-3xl md:text-5xl font-bold mb-6">
          Ready to Transform Your Business?
        </p>
        <ContactPopup />
      </motion.div>
    </section>
  );
};

export default ServiceAreas;